import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultArray } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';

import componentStyles from './pc-menu.css.lit';
import globalStyles from './pc-menu.lightDom.css.lit';

/**
 * Menu component
 * @element pc-menu
 * @slot - This element has a default slot
 */
@customElement('pc-menu')
export class PCMenu extends OutlineElement {
  adoptedStylesheets: AdoptedStyleSheets;
  static styles: CSSResultArray = [componentStyles];

  @property({ type: Boolean, attribute: 'is-expanded', reflect: true })
  isExpanded = false;

  items: Array<Element> = [];

  _overlay: Element;

  connectedCallback() {
    super.connectedCallback();
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);

    const link = this.querySelector('[slot="primary"]')?.children ?? [];
    const ancillarySlot = this.children[2];

    this.items = [...link];

    if (ancillarySlot) {
      this.items.concat(ancillarySlot);
    }

    this._createOverlay();
  }

  _createOverlay() {
    let overlayElement = document.querySelector('.overlay-menu');

    if (!overlayElement) {
      const bodyElement = document.querySelector('body');
      overlayElement = document.createElement('div');
      overlayElement.classList.add('overlay-menu');
      bodyElement?.prepend(overlayElement);
    }

    this._overlay = overlayElement;
  }

  private showOverlay() {
    this._overlay.classList.add('is-visible');
  }

  private hideOverlay() {
    this._overlay.classList.remove('is-visible');
  }

  private _collapseItemHandler() {
    this.isExpanded = false;
    this.hideOverlay();
    this.items.forEach((item: Element) => item.classList?.remove('is-hidden'));
  }

  private _expandItemHandler(event: CustomEvent) {
    this.isExpanded = true;
    this.showOverlay();
    const element = event.target as Element;

    if (element.classList.contains('is-hidden')) {
      element.classList.remove('is-hidden');
    }

    // Reset elements.
    this.items
      .filter(item => item !== element)
      .forEach((item: Element) => {
        item.classList?.add('is-hidden');
        item.removeAttribute('is-expanded');

        item
          .querySelectorAll('.is-hidden')
          .forEach(item => item.classList.remove('is-hidden'));

        item
          .querySelectorAll('[is-expanded]')
          .forEach(item => item.removeAttribute('is-expanded'));
      });
  }

  render(): TemplateResult {
    return html`
      <div
        class="menu"
        @collapse-item=${this._collapseItemHandler}
        @expand-item=${this._expandItemHandler}
      >
        <slot name="primary"></slot>
        <slot name="ancillary"></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-menu': PCMenu;
  }
}
